(ns app.front.notifications
  (:require [app.front.entities :as entities]
            [app.front.util :as futil]
            [clojure.edn :as edn]
            [core.front.comm :as comm]
            [core.http]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema-tools.core :as st]
            [schema.core :as s]))

(s/defschema Notification
  (st/merge
    {:id      s/Str
     :type    (s/enum :inline :toast)
     :kind    (s/enum :error :info :success :warning)
     :title   (s/cond-pre s/Keyword s/Str)
     :caption (s/cond-pre s/Keyword s/Str [s/Any])}
    (st/optional-keys
      {:child             s/Any
       :class-name        s/Str
       :hide-close-button s/Bool})))

(def valid-notification (s/checker Notification))

;; -- Subscriptions -----------------------------------------------------------

#_{:clj-kondo/ignore [:re-frame.reg-sub/unnamespaced-key]}
(rf/reg-sub :notifications
  :-> :notifications)

#_{:clj-kondo/ignore [:re-frame.reg-sub/unnamespaced-key]}
(rf/reg-sub :inline-notifications
  :<- [:notifications]
  (fn [notifications] (filter #(= :inline (:type %)) notifications)))

#_{:clj-kondo/ignore [:re-frame.reg-sub/unnamespaced-key]}
(rf/reg-sub :toast-notifications
  :<- [:notifications]
  (fn [notifications] (filter #(= :toast (:type %)) notifications)))

;; -- Events ------------------------------------------------------------------

(def debug (rf/after (fn [db event]
                       (.log js/console "=======")
                       (.log js/console "notifications: " (str (-> db :notifications)))
                       (.log js/console "event: " (str event)))))

(def interceptors [(when ^boolean js/goog.DEBUG debug)])

(defn handle-add
  [db [_ type-or-options kind title message id]]
  (let [notification (if (map? type-or-options)
                       type-or-options
                       {:id      (or id (str "notification#" (random-uuid)))
                        :type    type-or-options
                        :kind    kind
                        :title   title
                        :caption message})]
    (if-let [invalid (valid-notification notification)]
      (do
        (js/console.error "Invalid notification:" (str invalid))
        db)
      (update db :notifications conj notification))))

(defn remove-by-id [coll id]
  (remove #(= (:id %) id) coll))

(defn handle-upsert
  [db args]
  (if-not (p/find-first (:notifications db) {:id (last args)})
    (handle-add db args)
    db))

(defn handle-remove
  [db [_ id]]
  (update db :notifications remove-by-id id))

(rf/reg-event-db :notifications/add interceptors handle-add)
(rf/reg-event-db :notifications/upsert interceptors handle-upsert)
(rf/reg-event-db :notifications/remove interceptors handle-remove)

(def frontend-version (futil/get-meta-content-by-name "vaahteraFrontendVersion"))

(defn- process-tr-args
  "The tr args themselves can be localizable tr inputs, translate them if necessary."
  [tr [terms & [arg-vector]]]
  (if (seq arg-vector)
    (->> arg-vector
         (mapv (fn [arg]
                 (cond
                   (vector? arg) (tr arg)
                   (keyword? arg) (tr [arg])
                   :else arg)))
         vector
         (cons terms))
    terms))

(defn- process-notification-data
  "Process notification data received from backend, updating db and returning dispatches."
  [db tr {:keys [notifications entities removed-entities reload-needed?]}]
  (cond
    (seq notifications)
    {:db (cond-> (-> (update db
                             :notifications
                             concat
                             (map (fn [{:notification/keys [type kind title subtitle caption caption-text
                                                            caption-tr-args action child-method parameters] :as not}]
                                    (when-not (= kind :silent)
                                      (p/assoc-if
                                        {:id       (:db/id not)
                                         :type     (or type :toast)
                                         :kind     kind
                                         :title    title
                                         :subtitle subtitle
                                         :caption  (or (and caption-tr-args (process-tr-args tr caption-tr-args))
                                                       caption-text
                                                       caption)}
                                        :action action
                                        :child-method child-method
                                        :parameters parameters)))
                                  notifications))
                     (update :app.front.entities/entity-db (fn [edb]
                                                             (apply dissoc edb removed-entities)))
                     (assoc ::last-t (-> notifications last :t)))
           entities (entities/override-entity-db entities))
     :fx (->> notifications
              (keep :notification/re-frame-event)
              distinct
              (mapv (fn [e] [:dispatch (edn/read-string e)])))}

    reload-needed?
    (when-not (p/find-first (:notifications db) {:id "reload-needed"})
      {:dispatch [:notifications/add {:type              :inline
                                      :kind              :warning
                                      :title             :notification/reload-needed
                                      :caption           :notification/version
                                      :id                "reload-needed"
                                      :class-name        "reload-needed-notification"
                                      :hide-close-button true
                                      :child             [:button.reload.text-white.font-semibold.ml-4.border.border-white.uppercase
                                                          {:on-click #(js/window.location.reload true)}
                                                          (tr [:notification/reload-now "Reload now"])]}]})))

;; -- SSE (Server-Sent Events) for live notifications ------------------------

(defn- close-event-source
  "Close the current EventSource if it exists."
  [db]
  (when-let [existing-es (::event-source db)]
    (.close existing-es)))

(rf/reg-event-fx ::start-sse-stream
  (fn [{:keys [db]} _]
    (when-not (::notifications-aborted db)
      ;; Close existing event source if any
      (close-event-source db)
      (let [since-t      (::last-t db) ;; may be nil
            event-source (comm/create-event-source
                           {:url             (comm/build-sse-url :notifications/get
                                                                 (p/assoc-if {:latest-commit frontend-version}
                                                                             :from since-t))
                            :event-name      "notification"
                            :on-message      (fn [data]
                                               (rf/dispatch [::sse-notification-received data]))
                            :on-error        (fn []
                                               (rf/dispatch [::sse-connection-error-debounce]))
                            :close-on-error? true})]
        {:db (assoc db ::event-source event-source)}))))

(rf/reg-event-fx ::sse-notification-received
  [(rf/inject-cofx :tr)]
  (fn [{:keys [db tr]} [_ data]]
    (process-notification-data db tr data)))

(rf/reg-event-fx ::sse-connection-error
  (fn [{:keys [db]} _]
    (when-not (::notifications-aborted db)
      (close-event-source db)
      {:db           (dissoc db ::event-source)
       ;; Do a regular HTTP request here to check if session is still active
       ;; Logic in comm/command will handle 401 response and redirect to login page. 200 means session is active.
       :comm/command {:command         :user/session-active
                      :method          :get
                      ;; If this succeeded, we can restart immediately
                      :on-success      [::start-sse-stream]
                      ;; If there was a connection or server, wait for 5 secs to try again
                      :on-server-error [::sse-connection-error-debounce]
                      :on-error        [::sse-connection-error-debounce]}})))

(rf/reg-event-fx ::sse-connection-error-debounce
  (fn [_ _]
    {;; Reconnect after a delay
     :dispatch-debounce [{:id      ::sse-reconnect
                          :timeout 5000
                          :action  :dispatch
                          :event   [::sse-connection-error]}]}))

;; -- Public API --------------------------------------------------------------

(rf/reg-event-fx ::abort-backend-notifications
  (fn [{:keys [db]} _]
    ;; Close SSE connection if exists
    (close-event-source db)
    {:db (-> db
             (dissoc ::event-source)
             (assoc ::notifications-aborted true))}))

(rf/reg-event-fx ::start-backend-notifications
  (fn [{:keys [db]} _]
    {:db       (assoc db ::notifications-aborted false)
     :dispatch [::start-sse-stream]}))
